// Client-side ack reconciler — the resilient driver.
//
// A periodic sweep that reconciles the client's own unconfirmed sends
// (`selectUnconfirmed`) against the WhatsApp store and pushes ONLY strictly
// monotonic ack advances upstream through the EXISTING report path. It
// self-heals the sends that stall because the live change:ack listener missed
// the ack (the message delivered, but the ack never left the page, so the
// reaper would false-mark it "abandoned").
//
// This module is context-agnostic and side-effect free at import time: it
// defines the pure per-doc reconcile (`reconcileOne`) and the sweep driver
// (`runSweep`), taking ALL store/report/scheduling access via injected deps so
// the logic is fully unit-testable. The concrete content-context wiring (the
// page ack-bridge resolver + the promote-pending-ack reporter) lives in
// `context/content/services/ack-reconciler-content.ts` and is what the content
// entry actually mounts.

import { ACK } from '@bt-common/schemas/wa/message-ack';
import {
  AckState,
  isMonotonicAdvance,
  knownAckStateFromDoc,
} from './ack-state';
import {
  selectUnconfirmed,
  StateWithUserMessages,
  UnconfirmedDoc,
} from './select-unconfirmed';

/** Resolve a doc to its current WhatsApp store ack state. MUST return null when
 *  the message is not in the local store — the reconciler NEVER force-loads
 *  from WhatsApp (that is the ban vector). */
export type StoreAckResolver = (doc: UnconfirmedDoc) => Promise<AckState | null> | AckState | null;

/** Push a confirmed monotonic advance upstream through the existing report
 *  path. Best-effort; a throw is caught per-doc by the driver. */
export type AckReporter = (doc: UnconfirmedDoc, store: AckState) => Promise<void> | void;

/**
 * Session-scoped memory of what we've already reported upstream, keyed by the
 * durable `messageId`. It exists because the monotonic guard is measured against
 * the LOCAL Redux doc's ack — and that ack does NOT advance after our own
 * promote lands (the confirm/delete patch may never reach this client; indeed
 * the same fragility that stalled the live ack can stall the patch). Without
 * this, `isMonotonicAdvance(store, staleDoc)` stays true and the reconciler
 * re-sends the identical ack every sweep, forever. The ledger makes de-dup a
 * property of the reconciler itself, independent of whether the dbReducer ever
 * refreshes. In-memory only (no persistence) — a reload reloads the docs fresh,
 * where a genuinely-still-pending send is worth reporting once more.
 */
export interface ReportLedger {
  /** Highest ack already reported upstream for this doc THIS session, or
   *  `-Infinity` if none. */
  lastAck(messageId: string): number;
  /** Record that `ack` was reported for `messageId`. */
  record(messageId: string, ack: number): void;
  /** Drop ledger entries whose doc is no longer in the unconfirmed set, so the
   *  map can't grow unbounded across a long-lived session. Called once per sweep
   *  with the ids seen this sweep. */
  retain(currentIds: Set<string>): void;
}

export interface ReconcileDeps {
  resolve: StoreAckResolver;
  report: AckReporter;
  /** Optional client-side de-dup ledger. When absent, every monotonic advance is
   *  reported (legacy behavior — relies on the dbReducer refreshing to stop). */
  ledger?: ReportLedger;
}

export interface SweepDeps extends ReconcileDeps {
  /** Read the current Redux state (for selectUnconfirmed). */
  getState: () => StateWithUserMessages | undefined;
  /** Schedule the NEXT sweep. Defaults to setTimeout. Injected for tests. */
  scheduleNext?: (fn: () => void, ms: number) => void;
  /** Cooperative yield to the main thread. Defaults to scheduler.yield() with a
   *  setTimeout(0) fallback. Injected for tests. */
  yieldFn?: () => Promise<void>;
  /** Monotonic clock for the time-slice budget. Defaults to performance.now /
   *  Date.now. Injected for tests. */
  now?: () => number;
  /** Gap between the END of one sweep and the START of the next (ms). */
  intervalMs?: number;
  /** Time-slice budget: yield to the event loop at least every this-many ms. */
  sliceMs?: number;
  /** Optional error sink for observability (never throws into the loop). */
  onError?: (context: string, error: unknown) => void;
}

/** Gap between sweeps: scheduled AFTER a run finishes (recursive setTimeout, not
 *  setInterval) so a slow sweep can never overlap the next. */
export const DEFAULT_INTERVAL_MS = 60_000;
/** Yield to the event loop at least this often while sweeping. */
export const DEFAULT_SLICE_MS = 5;
/** Confirm threshold — matches the live path (ack >= SERVER promotes). */
export const CONFIRM_THRESHOLD = ACK.SERVER;

function defaultNow(): number {
  const perf = (globalThis as { performance?: { now?: () => number } }).performance;
  return typeof perf?.now === 'function' ? perf.now() : Date.now();
}

function defaultYield(): Promise<void> {
  const s = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  return typeof s?.yield === 'function' ? s.yield() : new Promise<void>((r) => setTimeout(r));
}

function defaultSchedule(fn: () => void, ms: number): void {
  setTimeout(fn, ms);
}

/**
 * Per-doc reconcile outcome. Reported by {@link reconcileOne} and aggregated by
 * {@link runSweep} so a live sweep self-explains WHY it did or didn't confirm —
 * the diagnostic that separates the two live failure hypotheses:
 *   - `skip-absent`         : resolve returned null (not in store / no ack yet /
 *                             no usable key) — the message isn't confirmably
 *                             delivered from this client's store.
 *   - `skip-below-threshold`: store ack < SERVER — reached the store but not yet
 *                             server-acked.
 *   - `skip-no-advance`     : the store ack is NOT ahead of what the doc already
 *                             carries — i.e. the ack DID reach the client but the
 *                             status was never promoted (a promotion bug, NOT a
 *                             resolve-returns-null bug).
 *   - `skip-already-reported`: the store advanced past the doc's stale local ack,
 *                             but we ALREADY reported this ack (or higher) for
 *                             this doc this session — a re-report the ledger
 *                             suppresses so a never-refreshing dbReducer can't
 *                             make us re-send the same ack every sweep.
 *   - `skip-error`          : resolve/report threw (counted by the driver).
 *   - `confirmed`           : a monotonic advance was pushed upstream.
 */
export type ReconcileOutcome =
  | 'confirmed'
  | 'skip-absent'
  | 'skip-below-threshold'
  | 'skip-no-advance'
  | 'skip-already-reported'
  | 'skip-error';

/**
 * Reconcile ONE doc: resolve its store message (skip-if-absent — never
 * force-load), read the store ack state, and report ONLY a strictly-monotonic
 * advance that has reached the confirm threshold. Idempotent: an
 * already-confirmed / unchanged doc pushes nothing.
 *
 * Returns the {@link ReconcileOutcome} classifying what happened (for the sweep
 * breakdown log + tests). `skip-error` is NOT returned here — a throw propagates
 * to the driver, which catches it and counts it.
 */
export async function reconcileOne(doc: UnconfirmedDoc, deps: ReconcileDeps): Promise<ReconcileOutcome> {
  const store = await deps.resolve(doc);
  // Not in the local store → skip. The reaper still ages out a genuinely
  // store-absent / never-acked message correctly.
  if (!store) return 'skip-absent';
  // Nothing has reached the server yet — no confirmation to push.
  if (store.ack < CONFIRM_THRESHOLD) return 'skip-below-threshold';

  const known = knownAckStateFromDoc(doc);
  // The store ack is not ahead of the doc's known ack — the ack reached the
  // client but the status wasn't promoted. Distinct from skip-absent.
  if (!isMonotonicAdvance(store, known)) return 'skip-no-advance';

  // De-dup: even though the store is ahead of the doc's LOCAL ack, we may have
  // already reported this ack for this doc (the local ack never advanced because
  // our own promote's confirm/delete patch never came back). Suppress the
  // re-send unless the store has genuinely climbed past what we last reported.
  const lastReported = deps.ledger?.lastAck(doc.id) ?? -Infinity;
  if (store.ack <= lastReported) return 'skip-already-reported';

  await deps.report(doc, store);
  deps.ledger?.record(doc.id, store.ack);
  return 'confirmed';
}

// Module-level re-entrancy flag. Belt-and-suspenders against a stray trigger
// (e.g. an MQTT-reconnect nudge) overlapping an in-flight sweep. Only one sweep
// touches it and it is cleared in `finally`, so it can never wedge.
let running = false;

/** Test/diagnostic accessor for the re-entrancy flag. */
export function isSweepRunning(): boolean {
  return running;
}

/**
 * Drive one full sweep over the ENTIRE unconfirmed set, then schedule the next
 * run `intervalMs` after this one finishes (recursive setTimeout). Time-sliced:
 * yields to the event loop whenever the slice budget elapses so a large sweep
 * never blocks the main thread. Re-entrant calls while a sweep is running are a
 * no-op (they do NOT schedule — the active run owns the next tick).
 */
export async function runSweep(deps: SweepDeps): Promise<void> {
  if (running) return; // re-entrancy guard — active run reschedules
  running = true;

  const now = deps.now ?? defaultNow;
  const doYield = deps.yieldFn ?? defaultYield;
  const sliceMs = deps.sliceMs ?? DEFAULT_SLICE_MS;

  // Per-outcome tally for the end-of-sweep breakdown log.
  const tally: Record<ReconcileOutcome, number> = {
    confirmed: 0,
    'skip-absent': 0,
    'skip-below-threshold': 0,
    'skip-no-advance': 0,
    'skip-already-reported': 0,
    'skip-error': 0,
  };
  let total = 0;

  try {
    const docs = selectUnconfirmed(deps.getState());
    total = docs.length;
    // Prune ledger entries for docs that have left the unconfirmed set so the
    // session-scoped memory can't grow without bound.
    deps.ledger?.retain(new Set(docs.map((d) => d.id)));
    let sliceStart = now();
    for (const doc of docs) {
      if (now() - sliceStart >= sliceMs) {
        await doYield();
        sliceStart = now();
      }
      try {
        tally[await reconcileOne(doc, deps)] += 1;
      } catch (error) {
        tally['skip-error'] += 1;
        deps.onError?.('reconcileOne', error);
      }
    }
    // Permanent observability: one line per sweep that has work, so a live rig
    // run reveals WHY confirmations did/didn't happen. `noAdvance` answers "did
    // the ack reach the client but not get promoted?"; `absent`+`belowThreshold`
    // answer "did the store lookup resolve at all?". Silent on an empty sweep.
    if (total > 0) {
      console.log(
        `[ack-reconciler] sweep: total=${total} confirmed=${tally.confirmed} ` +
          `absent=${tally['skip-absent']} belowThreshold=${tally['skip-below-threshold']} ` +
          `noAdvance=${tally['skip-no-advance']} alreadyReported=${tally['skip-already-reported']} ` +
          `error=${tally['skip-error']}`,
      );
    }
  } catch (error) {
    deps.onError?.('runSweep', error);
  } finally {
    running = false;
    const schedule = deps.scheduleNext ?? defaultSchedule;
    schedule(() => {
      void runSweep(deps);
    }, deps.intervalMs ?? DEFAULT_INTERVAL_MS);
  }
}

/** Kick off the recursive sweep loop. Safe to call once at mount. */
export function startAckReconciler(deps: SweepDeps): void {
  void runSweep(deps);
}
